/**
 * Create marketing banner configuration row.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody } from '@/components/common/Card';
import { MarketingBannerForm } from '@/components/marketingBanners/MarketingBannerForm';
import { marketingBannerService } from '@/app/services/marketingBannerService';
import {
  BLANK_MARKETING_BANNER,
  type CreateMarketingBannerRequest,
} from '@/app/types/marketingBanners';
import { normalizeMarketingBannerRequest } from '@/app/utils/normalizeMarketingBannerRequest';
import { logger } from '@/app/utils/logger';

import '../MarketingBannerEdit/MarketingBannerEdit.scss';

export const MarketingBannerCreate: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CreateMarketingBannerRequest>(BLANK_MARKETING_BANNER);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const created = await marketingBannerService.createBanner(
        normalizeMarketingBannerRequest(formData),
      );
      navigate(`/marketing-banners/${created.id}/edit`);
    } catch (err: unknown) {
      logger.error('Failed to create marketing banner', { error: err });
      const detail =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ===
          'string'
          ? (err as { response: { data: { detail: string } } }).response.data.detail
          : 'Failed to create marketing banner.';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="marketing-banner-edit" onSubmit={(e) => void handleSubmit(e)}>
      {error && (
        <div className="marketing-banner-edit__error" role="alert">
          {error}
        </div>
      )}
      <MarketingBannerForm value={formData} onChange={setFormData} disabled={saving} />
      <Card>
        <CardBody>
          <div className="marketing-banner-edit__actions">
            <button type="button" className="btn-secondary" onClick={() => navigate('/marketing-banners')} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create banner'}
            </button>
          </div>
        </CardBody>
      </Card>
    </form>
  );
};

export default MarketingBannerCreate;
